/**
 * UserManagement Use Case
 * 사용자 관리 상태 및 로직
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { User, UserCreate } from '../models/UserManagement';
import { userManagementRepository } from '../repositories/UserManagementRepository';
import { userManagementService } from '../services/UserManagementService';

export const useUserManagement = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const [newUser, setNewUser] = useState<UserCreate>({
    name: '',
    phone: '',
    email: '',
  });

  /**
   * 사용자 목록 로드
   */
  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await userManagementRepository.getUsers();
      setUsers(data);
    } catch (err: any) {
      console.error('사용자 데이터 로딩 실패:', err);
      setError(err.message || '사용자 데이터 로딩 실패');
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 초기 데이터 로드
   */
  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  /**
   * 필터링된 사용자 목록
   */
  const filteredUsers = useMemo(() => {
    return userManagementService.filterUsers(users, searchTerm);
  }, [users, searchTerm]);

  /**
   * 사용자 상세 보기
   */
  const handleViewUser = useCallback((user: User) => {
    setSelectedUser(user);
    setShowUserModal(true);
  }, []);

  /**
   * 사용자 생성 모달 열기
   */
  const openCreateModal = useCallback(() => {
    setNewUser({
      name: '',
      phone: '',
      email: '',
    });
    setShowCreateModal(true);
  }, []);

  /**
   * 사용자 생성
   */
  const handleCreateUser = useCallback(async () => {
    const validation = userManagementService.validateUserForm(
      newUser.name,
      newUser.phone,
      newUser.email
    );

    if (!validation.isValid) {
      alert(validation.error);
      return;
    }

    try {
      await userManagementRepository.createUser(newUser);
      alert('사용자가 생성되었습니다.');
      setShowCreateModal(false);
      await loadUsers();
    } catch (error) {
      console.error('사용자 생성 실패:', error);
      alert('사용자 생성에 실패했습니다.');
    }
  }, [newUser, loadUsers]);

  /**
   * 사용자 삭제
   */
  const handleDeleteUser = useCallback(
    async (userId: string) => {
      if (!window.confirm('정말 이 사용자를 삭제하시겠습니까?')) {
        return;
      }

      try {
        await userManagementRepository.deleteUser(userId);
        alert('사용자가 삭제되었습니다.');
        await loadUsers();
      } catch (error) {
        console.error('사용자 삭제 실패:', error);
        alert('사용자 삭제에 실패했습니다.');
      }
    },
    [loadUsers]
  );

  /**
   * 사용자 활성화/비활성화 토글
   */
  const handleToggleActive = useCallback(
    async (userId: string, currentActive: boolean) => {
      try {
        await userManagementRepository.toggleUserActive(userId, !currentActive);
        await loadUsers();
      } catch (error) {
        console.error('사용자 상태 변경 실패:', error);
        alert('사용자 상태 변경에 실패했습니다.');
      }
    },
    [loadUsers]
  );

  /**
   * 사용자 모달 닫기
   */
  const closeUserModal = useCallback(() => {
    setShowUserModal(false);
    setSelectedUser(null);
  }, []);

  /**
   * 사용자 생성 모달 닫기
   */
  const closeCreateModal = useCallback(() => {
    setShowCreateModal(false);
    setNewUser({
      name: '',
      phone: '',
      email: '',
    });
  }, []);

  return {
    // 상태
    users,
    loading,
    error,
    searchTerm,
    setSearchTerm,
    selectedUser,
    showUserModal,
    showCreateModal,
    newUser,
    setNewUser,
    filteredUsers,

    // 액션
    handleViewUser,
    openCreateModal,
    handleCreateUser,
    handleDeleteUser,
    handleToggleActive,
    closeUserModal,
    closeCreateModal,
  };
};
